import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { PapiDeviation } from '@wix/da-papi-types';
import { Url, URLS } from '@wix/da-url';
import type { NullableDropdownItem } from '@wix/da-ds/pkg/Dropdown/types';
import {
  BiData,
  TierEditClickBiEvent,
  BiContextProvidedValue,
  TierDeleteBiEvent,
  ClickCopyBiEvent,
  ManageSubscriptionBiEvent,
  SubscriberConnectToDiscordClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import {
  editDeviation,
  shareDeviationToPost,
} from '../../redux/deviations/actionCreators';
import { DeviationResourceType } from '../../types/deviation';
import { useVerification } from '../../VerificationPopup/withVerification';
import { useCopyDeviationLinkClick } from '../../utils/hooks/useCopyLinkClick';
import { deleteTier } from '../redux/creator/actions';
import { getIsReduxInitialized as getIsDiscordReduxInitialized } from '../../Discord/redux/selectors';
import { useSubscriptionTier } from '../../utils/hooks/useSubscriptionTier';
import { subscriberConnect } from '../../Discord/redux/actions';

function useEditTierAction(deviation: PapiDeviation): NullableDropdownItem {
  const { t } = useTranslation();
  const dispatchEditDeviation = useAction(editDeviation);
  const handleEditTier = useCallback(() => {
    dispatchEditDeviation({ deviation });
  }, [dispatchEditDeviation, deviation]);

  return {
    label: t('common.edit'),
    onClick: handleEditTier,
    biData: BiData<TierEditClickBiEvent>({
      evid: 536,
      typeid: DeviationResourceType.TIER,
      itemid: deviation.deviationId,
    }),
  };
}

function useDeleteTierAction(
  deviation: PapiDeviation,
  afterActionUrl?: string
): NullableDropdownItem {
  const { t } = useTranslation();
  const dispatchDeleteTier = useAction(deleteTier);
  const handleDeleteTier = useCallback(() => {
    dispatchDeleteTier({ deviation, redirectTo: afterActionUrl });
  }, [dispatchDeleteTier, deviation, afterActionUrl]);

  return {
    label: t('common.delete'),
    onClick: handleDeleteTier,
    biData: BiData<TierDeleteBiEvent>({
      evid: 541,
      typeid: DeviationResourceType.TIER,
      itemid: deviation.deviationId,
      has_subscribers: (deviation.tier?.stats?.subscribers ?? 0) > 0 ? 1 : 0,
    }),
  };
}

function useGoToTierAction(deviation: PapiDeviation): NullableDropdownItem {
  const { t } = useTranslation();
  return {
    label: t('deviationview.tierCard.moreActionsMenu.item.goToTierPage'),
    onClick: () => {
      if (deviation.url) {
        window.location.href = deviation.url;
      }
    },
  };
}

function useGoToTierDashboardAction(
  deviation: PapiDeviation
): NullableDropdownItem {
  const { t } = useTranslation();
  return {
    label: t('deviationview.tierCard.moreActionsMenu.item.goToTierDashboard'),
    onClick: () => {
      window.location.href = 
        `${Url.buildFromPath(URLS.CUSTOMER_DASHBOARD)}?subproductid=${
          deviation.tier?.subproductId
        }`;
    },
  };
}

function useShareTierToPost(deviation: PapiDeviation): NullableDropdownItem {
  const { t } = useTranslation();
  const dispatchShareDeviationToPost = useAction(shareDeviationToPost);
  const handleShareDeviationToPost = useCallback(() => {
    dispatchShareDeviationToPost(deviation);
  }, [dispatchShareDeviationToPost, deviation]);

  const { restrictToVerifiedAndLoggedIn } = useVerification();
  const restrictedShareToPost = restrictToVerifiedAndLoggedIn(
    ReferralType.SHARE_TO_POST,
    'submitstatus',
    handleShareDeviationToPost
  );

  return {
    label: t('deviationview.tierCard.moreActionsMenu.item.shareInPost'),
    onClick: restrictedShareToPost,
  };
}

function useShareTierToSocials(deviation: PapiDeviation): NullableDropdownItem {
  const { t } = useTranslation();
  const { onCopyLinkClick } = useCopyDeviationLinkClick(deviation);

  return {
    label: t('share.copyLink'),
    onClick: onCopyLinkClick,
    biData: BiData<ClickCopyBiEvent>({
      evid: 357,
      typeid: DeviationResourceType.TIER,
      itemid: deviation.deviationId,
      sectionname: BiContextProvidedValue,
    }),
  };
}

function useConnectDiscordAction(
  deviation: PapiDeviation
): NullableDropdownItem {
  const { t } = useTranslation();
  const dispatchSubscriberConnect = useAction(subscriberConnect);
  const isDiscordReduxInitialized = useSelector(getIsDiscordReduxInitialized);

  const handleClick = useCallback(() => {
    dispatchSubscriberConnect(deviation);
  }, [dispatchSubscriberConnect, deviation]);

  return {
    label: t('deviationview.tierCard.moreActionsMenu.item.connectDiscord'),
    onClick: handleClick,
    disabled: isDiscordReduxInitialized,
    biData: BiData<SubscriberConnectToDiscordClickBiEvent>({
      evid: 555,
      itemid: deviation.deviationId,
    }),
  };
}

function useManageTierForSubscriberAction(
  deviation: PapiDeviation
): NullableDropdownItem {
  const { t } = useTranslation();

  return {
    label: t('deviationview.tierCard.moreActionsMenu.item.unsubscribe'),
    link: Url.account(deviation.deviationId),
    biData: BiData<ManageSubscriptionBiEvent>({
      evid: 560,
      subscription_type: 'subscription',
    }),
  };
}

export function useTierContextMenuItems(
  deviation: PapiDeviation,
  isOwner: boolean,
  studioOrdering: boolean,
  afterActionUrl?: string
): NullableDropdownItem[] {
  const { isUserSubscribed } = useSubscriptionTier(deviation);
  const goToTierAction = useGoToTierAction(deviation);
  const goToTierDashboardAction = useGoToTierDashboardAction(deviation);
  const editAction = useEditTierAction(deviation);
  const deleteAction = useDeleteTierAction(deviation, afterActionUrl);
  const shareToPostAction = useShareTierToPost(deviation);
  const shareToSocialsAction = useShareTierToSocials(deviation);
  const manageTierForSubscriberAction =
    useManageTierForSubscriberAction(deviation);
  const connectDiscordAction = useConnectDiscordAction(deviation);

  const isDraft = deviation.tier?.state === 'draft';
  const isPendingDeletion = deviation.tier?.state === 'pending_deletion';

  if (isOwner && !studioOrdering) {
    return [
      !isDraft && goToTierAction,
      !isDraft && goToTierDashboardAction,
      editAction,
      !isDraft && shareToPostAction,
      !isDraft && shareToSocialsAction,
      !isPendingDeletion && deleteAction,
    ];
  } else if (isOwner && studioOrdering) {
    return [
      editAction,
      !isDraft && goToTierAction,
      !isDraft && goToTierDashboardAction,
      !isDraft && shareToPostAction,
      !isDraft && shareToSocialsAction,
      !isPendingDeletion && deleteAction,
    ];
  } else {
    const isTierConnectedToAuthorsDiscord =
      !!deviation.tier?.discord?.connected;
    const shouldShowDiscordAction =
      isUserSubscribed &&
      isTierConnectedToAuthorsDiscord &&
      !!deviation.tier?.discord?.oauth2;
    return [
      goToTierAction,
      shareToSocialsAction,
      shouldShowDiscordAction && connectDiscordAction,
      isUserSubscribed && manageTierForSubscriberAction,
    ];
  }
}
